<template>
    <!-- ============================================================
     * DefaultCampaign (기본 템플릿)
     * - CampaignEntry에서 내려준 campaign(props) 사용
     * - 발급은 provider(OB2/OB3)에 따라 /OpenBadges 또는 /OpenBadgesV3 호출
     * ============================================================ -->
    <div class="sub-page">
        <!-- Title -->
        <p
            class="text-center ma-auto pl-4 pr-4"
            style="color:#090d54; max-width: 950px;"
            :class="{ 'pt-15 le-heading5': $vuetify.breakpoint.smAndUp, 'pt-10 le-heading5': $vuetify.breakpoint.xsOnly }"
            v-html="titleHtml"
        ></p>
  
        <!-- Badge Image / Link -->
        <section class="c-section d-flex justify-center pt-0">
            <div class="c-contanier">
                <div
                    class="d-flex align-center justify-center"
                    :class="{ 'py-16': $vuetify.breakpoint.smAndUp }"
                    :style="$vuetify.breakpoint.xsOnly ? { paddingTop:'6vh', paddingBottom:'6vh' } : {}"
                >
                    <div>
                        <a
                            v-if="detailLinkUrl"
                            :href="detailLinkUrl"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="d-flex"
                        >
                            <v-img
                                :src="imageUrl"
                                :width="$vuetify.breakpoint.mdAndUp ? 220 : ($vuetify.breakpoint.smOnly ? 220 : 160)"
                            />
                        </a>
  
                        <v-img
                            v-else
                            :src="imageUrl"
                            :width="$vuetify.breakpoint.mdAndUp ? 220 : ($vuetify.breakpoint.smOnly ? 220 : 160)"
                        />
                    </div>
                </div>
  
                <p class="le-text-subtitle2 text-center">
                    <template v-if="detailLinkUrl">
                        {{ $t('trialBadgeCampaign.section1.subtitle') }}
                        <br>
                    </template>
                    {{ $t('trialBadgeCampaign.section1.subtitle2') }}
                </p>
  
                <!-- Form -->
                <div class="mt-10">
                    <v-form ref="form">
                        <v-row
                            no-gutters
                            class="d-flex flex-column justify-center align-center"
                        >
                            <v-col
                                cols="10"
                                md="4"
                                sm="8"
                                class="text-right mb-3"
                            >
                                <p class="text-red1">
                                    {{ $t('trialBadgeCampaign.section1.requiredNote') }}
                                </p>
                            </v-col>
  
                            <!-- Name -->
                            <v-col
                                cols="10"
                                md="4"
                                sm="8"
                                class="pb-0 pt-0 mb-2"
                            >
                                <p class="contact_title mb-3">
                                    <strong class="le-text-subtitle3">{{ $t('trialBadgeCampaign.section1.labelName') }}</strong>
                                    <span class="le-text-body2 text-red1 ml-1">*</span>
                                </p>
                                <v-text-field
                                    v-model="name"
                                    :rules="[v => !!v || $t('trialBadgeCampaign.errors.nameRequired')]"
                                    outlined
                                    color="blue1"
                                    :dense="$vuetify.breakpoint.smAndDown"
                                />
                            </v-col>
                        </v-row>
  
                        <!-- Email -->
                        <v-row
                            no-gutters
                            class="d-flex flex-column justify-center align-center"
                        >
                            <v-col
                                cols="10"
                                md="4"
                                sm="8"
                                class="pb-0 pt-0 mb-2"
                            >
                                <p class="contact_title mb-3">
                                    <strong class="le-text-subtitle3">{{ $t('trialBadgeCampaign.section1.labelEmail') }}</strong>
                                    <span class="le-text-body2 text-red1 ml-1">*</span>
                                </p>
                                <v-text-field
                                    v-model="email"
                                    :rules="[
                                        v => !!v || $t('trialBadgeCampaign.errors.emailRequired'),
                                        v => /.+@.+\..+/.test(v) || $t('trialBadgeCampaign.errors.emailInvalid')
                                    ]"
                                    outlined
                                    color="blue1"
                                    :placeholder="$t('trialBadgeCampaign.section1.placeholderEmail')"
                                    :dense="$vuetify.breakpoint.smAndDown"
                                />
                            </v-col>
                        </v-row>
  
                        <!-- Button -->
                        <v-row :class="{ 'mt-10': $vuetify.breakpoint.smAndUp, 'mt-3': $vuetify.breakpoint.xsOnly }">
                            <v-col class="d-flex align-center justify-center pt-0 pb-0">
                                <v-btn
                                    color="orange1"
                                    :large="$vuetify.breakpoint.smAndUp"
                                    depressed
                                    class="text-white1"
                                    @click="showConfirm"
                                >
                                    {{ $t('trialBadgeCampaign.section1.btnIssue') }}
                                </v-btn>
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
            </div>
        </section>
  
        <!-- Confirm Dialog -->
        <v-dialog
            v-model="confirmOpen"
            width="500"
        >
            <v-card class="pa-0 rounded-lg">
                <div class="le-heading6 pa-5">
                    {{ $t('trialBadgeCampaign.dialogConfirm.title') }}
                </div>
  
                <div
                    v-if="loading"
                    class="center-absolute"
                >
                    <v-progress-circular
                        indeterminate
                        color="orange1"
                        size="60"
                        width="10"
                    />
                </div>
  
                <div
                    class="le-text-body2 pl-5 pr-6"
                    style="min-height:88px;"
                >
                    <p class="mb-3">
                        {{ $t('trialBadgeCampaign.dialogConfirm.intro') }}
                    </p>
                    <strong>{{ name }}</strong><br />
                    <strong>{{ email }}</strong><br />
                    <p class="mt-3">
                        {{ $t('trialBadgeCampaign.dialogConfirm.question') }}
                    </p>
                </div>
  
                <div class="pa-5 d-flex justify-end gap-3">
                    <v-btn
                        color="blue1"
                        outlined
                        :small="$vuetify.breakpoint.smAndDown"
                        depressed
                        class="text-white1"
                        @click="confirmOpen=false"
                    >
                        {{ $t('trialBadgeCampaign.dialogConfirm.btnNo') }}
                    </v-btn>
                    <v-btn
                        color="orange1"
                        :small="$vuetify.breakpoint.smAndDown"
                        depressed
                        class="text-white1"
                        :class="{disabled:loading}"
                        @click="submit"
                    >
                        {{ $t('trialBadgeCampaign.dialogConfirm.btnYes') }}
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
  
        <!-- Done Dialog -->
        <v-dialog
            v-model="doneOpen"
            width="500"
        >
            <v-card class="pa-0 rounded-lg">
                <div class="le-heading6 pa-5">
                    {{ $t('trialBadgeCampaign.dialogDone.title') }}
                </div>
                <div
                    class="le-text-body2 pl-5 pr-6"
                    style="min-height:88px;"
                    v-html="$t('trialBadgeCampaign.dialogDone.body')"
                ></div>
                <div class="pa-5 d-flex justify-end gap-3">
                    <v-btn
                        color="orange1"
                        :small="$vuetify.breakpoint.smAndDown"
                        depressed
                        class="text-white1"
                        @click="doneOpen=false"
                    >
                        {{ $t('trialBadgeCampaign.dialogDone.btnOk') }}
                    </v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>
  
<script>
import campaignIssueMixin from '@/views/campaigns/_shared/campaignIssueMixin';
  
export default {
    name: 'DefaultCampaign',
    mixins: [campaignIssueMixin],
};
</script>
  